export type Formatter = {
    currency: ReturnType<typeof useCurrencyFormatter>['formatCurrency']
    number: ReturnType<typeof useNumberFormatter>['formatNumber']
    date: ReturnType<typeof useDateFormatter>['formatDate']
}

export function useCurrencyFormatter(options: {
    locale: MaybeRefOrGetter<string>
    country: MaybeRefOrGetter<string>
    currency: MaybeRefOrGetter<string>
}) {

    const formatter = computed(() => {
        const locale = toValue(options.locale)
        const country = toValue(options.country)
        const currency = toValue(options.currency)

        return new Intl.NumberFormat(`${locale}-${country}`, {
            style: 'currency',
            currency: currency,
        })
    })

    function formatCurrency(value: number | FormattedCurrency | Monetary | null | undefined) {
        const numericValue = typeof value === 'number' ? value : getPriceValue(value)
        if (numericValue === null) {
            return '-'
        }
        return formatter.value.format(numericValue)
    }

    return {
        formatCurrency,
    }
}

export function useNumberFormatter(options: {
    locale: MaybeRefOrGetter<string>
    country: MaybeRefOrGetter<string>
}) {
    const formatter = computed(() => {
        const locale = toValue(options.locale)
        const country = toValue(options.country)

        return {
            singleFixedDigit: new Intl.NumberFormat(`${locale}-${country}`, {
                maximumFractionDigits: 1,
                minimumFractionDigits: 1,
            }),
            twoDigits: new Intl.NumberFormat(`${locale}-${country}`, {
                maximumFractionDigits: 2,
            }),
        }
    })

    function formatNumber(value: number | null | undefined, options?: { fixedFractionDigits?: 1 }) {
        if (value === null || value === undefined) {
            return '-'
        }
        if (options?.fixedFractionDigits === 1) {
            return formatter.value.singleFixedDigit.format(value)
        }

        return formatter.value.twoDigits.format(value)
    }

    return {
        formatNumber,
    }
}

export function useDateFormatter(options: {
    locale: MaybeRefOrGetter<string>
    country: MaybeRefOrGetter<string>
}) {
    const formatter = computed(() => {
        const locale = toValue(options.locale)
        const country = toValue(options.country)

        return {
            default: new Intl.DateTimeFormat(`${locale}-${country}`, {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
            }),
            longMonth: new Intl.DateTimeFormat(`${locale}-${country}`, {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
            }),
        }
    })

    function formatDate(value: string | Date | null | undefined, options?: { type?: 'long-month' }) {
        if (!value) {
            return '-'
        }
        const date = typeof value === 'string' ? new Date(value) : value

        if (options?.type === 'long-month') {
            return formatter.value.longMonth.format(date)
        }
        return formatter.value.default.format(date).replace(/\s/g, '')
    }

    return {
        formatDate,
    }
}
